/* ----------------------------------------------------------------------------
 * Module connection-status taxonomy — the SINGLE, honest vocabulary for how
 * every engine/module/connector's real state is described to the operator and
 * to Aura. Provider-neutral. It exists so no surface can imply "all connected"
 * and so a BUILT-BUT-GATED connector is never conflated with a PLANNED one (the
 * raw engine registry status `coming` cannot tell them apart).
 *
 * SOURCE OF TRUTH (do not invent — encode only what these state):
 *   docs/brain/architecture/aura-modules-reconciliation-2026-07-19.md
 *   (+ the BookSesh operational-connector deliverable/addendum, same date).
 *
 * Key honest facts encoded here:
 *   - SEO is LIVE end-to-end.
 *   - Sales/CRM, Marketing, Content Studio, Social are live for their real
 *     read/draft surfaces; publish/send/execute stay capability-gated.
 *   - Automation approvals engine is BUILT + tested but NOT live (routes are
 *     fail-closed / 404, owner gates unopened) => built-owner-gated.
 *   - The BookSesh operational connector (a.k.a. Omega Hub / tenant T19) is
 *     built + pre-live verified but NOT live (G2/G3/G4/G-BS-DEPLOY unopened).
 *     Aura currently has NO Omega Hub / T19 appointment or operational data and
 *     MUST NOT imply otherwise => built-owner-gated, dataVisibleToAura=false.
 *   - Analytics, Focus and the reserved engines are PLANNED / not yet built.
 * -------------------------------------------------------------------------- */

import { ENGINES, isLive, type Engine } from '../engines/registry.ts';

/** The four — and only four — honest module/connector states (owner directive). */
export type ModuleConnectionState =
  | 'live-and-connected' // actually wired + serving real data now
  | 'built-owner-gated' // code built + verified, NOT live (owner gates unopened)
  | 'planned-not-built' // design only
  | 'temporarily-unavailable'; // normally live but currently erroring / degraded

export interface ModuleConnectionStatus {
  state: ModuleConnectionState;
  /** Short honest label shown wherever a module's connection state is surfaced. */
  label: string;
  /** One honest sentence: what Aura may / may not do with this module. */
  note: string;
  /** True ONLY for modules whose real data Aura can actually see right now. */
  dataVisibleToAura: boolean;
}

/** Human labels — deliberately never "all connected"/"everything live". */
export const STATE_LABEL: Record<ModuleConnectionState, string> = {
  'live-and-connected': 'Live',
  'built-owner-gated': 'Built · owner-gated',
  'planned-not-built': 'Planned',
  'temporarily-unavailable': 'Temporarily unavailable',
};

/**
 * Engines whose registry status is `coming` but which are actually BUILT and
 * verified (inert only because an owner gate is unopened) — so they must read
 * as built-owner-gated, never as a not-yet-built "coming soon".
 * (Automation approvals engine: built + 78/78 tests, routes fail-closed.)
 */
const BUILT_BUT_GATED_ENGINE_KEYS = new Set<string>(['automation']);

/** Derive an engine's honest state from its registry status + the gated set. */
export function engineConnectionState(engine: Pick<Engine, 'key' | 'status'>): ModuleConnectionState {
  if (isLive(engine.status)) return 'live-and-connected';
  if (engine.status === 'coming') {
    return BUILT_BUT_GATED_ENGINE_KEYS.has(engine.key) ? 'built-owner-gated' : 'planned-not-built';
  }
  return 'planned-not-built'; // 'reserved'
}

/** The honest short label for an engine's connection state. */
export function engineStatusLabel(engine: Pick<Engine, 'key' | 'status'>): string {
  return STATE_LABEL[engineConnectionState(engine)];
}

/** Per-engine honest note (Aura's real capability), keyed by engine key. */
const ENGINE_NOTE: Record<string, string> = {
  seo: 'Live end-to-end — Aura reads real SEO evidence and can propose content actions.',
  sales: 'Live — Aura reads real leads, pipeline and sending health. Automated write-back is built but owner-gated (not active).',
  marketing: 'Live — real segments and campaigns; Aura drafts campaigns. Sending is connector-gated (not connected).',
  social: 'Live — Content Studio drafting from real data. Publishing and scheduling are connector-gated (not connected).',
  communications: 'Live — unified contact timeline (email now); further channels later.',
  ai: 'Live — advisory copilot, always human-in-control; it proposes, you decide.',
  automation: 'Built and tested but NOT live — the approvals/execute engine is owner-gated (routes fail-closed). Aura cannot execute actions.',
  analytics: 'Planned — cross-engine analytics is designed, not yet built.',
};

/**
 * Non-engine connectors that are NOT rendered as engines in the rail but whose
 * honest state must still be classifiable (and lockable by tests). Keeping the
 * BookSesh operational connector here — and OUT of the engine rail — is how we
 * ensure Aura never implies it can see Omega Hub / T19 operational data.
 */
export const CONNECTOR_STATUS: Record<string, ModuleConnectionStatus> = {
  'booksesh-operational': {
    state: 'built-owner-gated',
    label: STATE_LABEL['built-owner-gated'],
    note:
      'BookSesh operational connector (Omega Hub / tenant T19) is built and pre-live verified but NOT live — owner gates unopened. Aura has NO BookSesh appointment or operational data and must not imply otherwise.',
    dataVisibleToAura: false,
  },
};

/** Resolve any module OR connector key to its full honest status. */
export function moduleConnectionStatus(key: string): ModuleConnectionStatus | undefined {
  if (key in CONNECTOR_STATUS) return CONNECTOR_STATUS[key];
  const engine = ENGINES.find((e) => e.key === key);
  if (!engine) return undefined;
  const state = engineConnectionState(engine);
  return {
    state,
    label: STATE_LABEL[state],
    note: ENGINE_NOTE[engine.key] ?? engine.description,
    // Only genuinely live engines expose real data to Aura today.
    dataVisibleToAura: state === 'live-and-connected',
  };
}

/** Convenience: are we asserting real, visible data for this module? */
export function auraSeesData(key: string): boolean {
  return moduleConnectionStatus(key)?.dataVisibleToAura === true;
}
